import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth, FIREBASE_READY } from "../firebase";
import { useApp } from "../contexts/AppContext";
import LogoSVG from "../components/LogoSVG";

const LEGACY = { u: "admin", p: "LHDA@2026" };

export default function AdminLogin() {
  const { setIsAdmin } = useApp();
  const navigate       = useNavigate();

  const [credential,   setCredential]   = useState("");
  const [password,     setPassword]     = useState("");
  const [err,          setErr]          = useState("");
  const [loading,      setLoading]      = useState(false);
  const [forgotMode,   setForgotMode]   = useState(false);
  const [resetEmail,   setResetEmail]   = useState("");
  const [resetSent,    setResetSent]    = useState(false);
  const [resetErr,     setResetErr]     = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const login = async () => {
    setErr("");
    setLoading(true);
    try {
      if (FIREBASE_READY) {
        await signInWithEmailAndPassword(auth, credential, password);
      } else {
        if (credential !== LEGACY.u || password !== LEGACY.p) throw new Error();
        setIsAdmin(true);
      }
      navigate("/admin");
    } catch {
      setErr(FIREBASE_READY ? "Invalid email or password." : "Invalid username or password.");
    }
    setLoading(false);
  };

  const sendReset = async () => {
    setResetErr("");
    if (!resetEmail) { setResetErr("Please enter your email address."); return; }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSent(true);
    } catch (e) {
      if (e.code === "auth/user-not-found" || e.code === "auth/invalid-credential") {
        setResetErr("No account found with that email address.");
      } else if (e.code === "auth/invalid-email") {
        setResetErr("Please enter a valid email address.");
      } else if (e.code === "auth/too-many-requests") {
        setResetErr("Too many requests. Please wait a few minutes and try again.");
      } else {
        setResetErr(`Could not send reset email (${e.code || "unknown"}). Please try again.`);
      }
    }
    setResetLoading(false);
  };

  const onKey    = e => e.key === "Enter" && login();
  const onKeyRst = e => e.key === "Enter" && sendReset();

  if (forgotMode) {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <div className="login-logo"><LogoSVG size={80}/></div>
          <div className="login-title">Reset Password</div>
          <div className="login-sub">Enter your email to receive a reset link</div>

          {resetSent ? (
            <div style={{ background:"#e8f5e9", border:"1.5px solid #a5d6a7", borderRadius:12, padding:"1.25rem", textAlign:"center", margin:"1rem 0" }}>
              <div style={{ fontSize:"1.75rem", marginBottom:".4rem" }}>📧</div>
              <div style={{ fontWeight:700, color:"#2e7d32", marginBottom:".3rem" }}>Reset Email Sent</div>
              <div style={{ fontSize:".83rem", color:"#4e6058" }}>
                Check <strong>{resetEmail}</strong> for a password reset link.
              </div>
            </div>
          ) : (
            <>
              {resetErr && <div className="login-err">{resetErr}</div>}
              <div className="field">
                <label>Email Address</label>
                <input type="email" value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  placeholder="your@email.com" onKeyDown={onKeyRst} autoFocus/>
              </div>
              <button className="btn-g" style={{ width:"100%", padding:".7rem" }} onClick={sendReset} disabled={resetLoading}>
                {resetLoading ? "Sending…" : "Send Reset Email →"}
              </button>
            </>
          )}
          <button className="btn-gray" style={{ width:"100%", marginTop:".65rem" }}
            onClick={() => { setForgotMode(false); setResetSent(false); setResetEmail(""); setResetErr(""); }}>
            ← Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-box">
        <div className="login-logo"><LogoSVG size={80}/></div>
        <div className="login-title">LHDA Admin</div>
        <div className="login-sub">Content Management System</div>
        {err && <div className="login-err">{err}</div>}
        <div className="field">
          <label>{FIREBASE_READY ? "Email Address" : "Username"}</label>
          <input
            type={FIREBASE_READY ? "email" : "text"}
            value={credential}
            onChange={e => setCredential(e.target.value)}
            placeholder={FIREBASE_READY ? "admin@example.com" : "Enter username"}
            onKeyDown={onKey}
          />
        </div>
        <div className="field">
          <label>Password</label>
          <input type="password" value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter password" onKeyDown={onKey}/>
        </div>
        <button className="btn-g" style={{ width:"100%", padding:".7rem" }} onClick={login} disabled={loading}>
          {loading ? "Signing in…" : "Sign In →"}
        </button>
        {FIREBASE_READY && (
          <button className="btn-gray"
            style={{ width:"100%", marginTop:".5rem", background:"none", border:"none", color:"var(--gd)", fontSize:".85rem", cursor:"pointer", padding:".4rem" }}
            onClick={() => { setForgotMode(true); setResetEmail(credential); }}>
            Forgot password?
          </button>
        )}
        <button className="btn-gray" style={{ width:"100%", marginTop:".65rem" }} onClick={() => navigate(-1)}>
          ← Back to Website
        </button>
      </div>
    </div>
  );
}
